import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Volume2, Mic, Square, BookOpen, Palette, FileText, Upload, Check, Camera, Award, Video, Radio, Clock, ExternalLink, Sparkles, Code2, PlayCircle, ChevronRight } from "lucide-react";
import DrawingCanvas from "./DrawingCanvas";
import {
  speechSupport, loadVoices, speak, cancelSpeak, createRecognizer,
  matchWords, tokenize, setVoicePrefs, createReadingSession, createAudioRecorder,
} from "./speech";

const SUBTABS = [
  { id: "read", label: "Read to Lead", Icon: BookOpen },
  { id: "art", label: "Art Studio", Icon: Palette },
  { id: "notes", label: "Notes & Lessons", Icon: FileText },
  { id: "life_skills", label: "Life Skills", Icon: Sparkles },
  { id: "coding", label: "Coding Hub", Icon: Code2 },
  { id: "webinars", label: "Webinars", Icon: Video },
];

export function webinarState(w) {
  const now = Date.now();
  const start = w.start_at ? new Date(w.start_at).getTime() : null;
  const end = w.end_at ? new Date(w.end_at).getTime() : null;
  if (start && now < start) return "scheduled";
  if (start && end) return now <= end ? "live" : "archived";
  if (start && !end) return now <= start + 3 * 3600 * 1000 ? "live" : "archived";
  return "scheduled";
}

export default function StudentPonderrLearn() {
  const [sub, setSub] = useState("read");
  const [settings, setSettings] = useState({ enabled_languages: [], default_voice_gender: "female" });
  const [subject, setSubject] = useState("all");
  const [passages, setPassages] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [notes, setNotes] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [lifeSkills, setLifeSkills] = useState([]);
  const [coding, setCoding] = useState([]);
  const reload = () => {
    api.get("/ponderr-learn/student/passages").then((r) => setPassages(r.data)).catch(() => {});
    api.get("/ponderr-learn/student/art-prompts").then((r) => setPrompts(r.data)).catch(() => {});
    api.get("/ponderr-learn/student/notes").then((r) => setNotes(r.data)).catch(() => {});
    api.get("/ponderr-learn/student/webinars").then((r) => setWebinars(r.data)).catch(() => {});
    api.get("/ponderr-learn/student/life-skills").then((r) => setLifeSkills(r.data?.courses || [])).catch(() => {});
    api.get("/student/coding").then((r) => setCoding(r.data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/ponderr-learn/settings").then((r) => setSettings(r.data)).catch(() => {});
    loadVoices();
    reload();
  }, []);
  useEffect(() => { setVoicePrefs(settings); }, [settings]);
  const subjects = useMemo(() => {
    const set = new Set();
    [...passages, ...prompts, ...notes].forEach((x) => x.subject_name && set.add(x.subject_name));
    return Array.from(set).sort();
  }, [passages, prompts, notes]);
  const filt = (arr) => subject === "all" ? arr : arr.filter((x) => x.subject_name === subject);
  return (
    <div className="space-y-5">
      <div className="nb-card p-4 bg-[#C4B5FD]">
        <div className="nb-label">PonderrLearn</div>
        <div className="display text-2xl font-bold">Read to Lead · Art Studio · Immersive Notes</div>
        <div className="flex gap-2 overflow-x-auto pb-1 mt-3" data-testid="spl-subject-pills">
          <Pill active={subject === "all"} onClick={() => setSubject("all")} testid="spl-pill-all">All</Pill>
          {subjects.map((s) => <Pill key={s} active={subject === s} onClick={() => setSubject(s)} testid={`spl-pill-${s}`}>{s}</Pill>)}
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {SUBTABS.map((t) => (
          <button key={t.id} onClick={() => setSub(t.id)}
            className={`nb-btn text-xs sm:text-sm shrink-0 ${sub === t.id ? "" : "nb-btn-ghost"}`}
            data-testid={`spl-subtab-${t.id}`}><t.Icon size={14} />{t.label}</button>
        ))}
      </div>
      {sub === "read" && <StudentRead passages={filt(passages)} settings={settings} onSubmitted={reload} />}
      {sub === "art" && <StudentArt prompts={filt(prompts)} onSubmitted={reload} />}
      {sub === "notes" && <StudentNotes notes={filt(notes)} settings={settings} />}
      {sub === "life_skills" && <StudentLifeSkills courses={lifeSkills} settings={settings} />}
      {sub === "coding" && <StudentCoding activities={coding} />}
      {sub === "webinars" && <StudentWebinars webinars={webinars} />}
      <LiveWebinarDock webinars={webinars} onJoinTab={() => setSub("webinars")} />
    </div>
  );
}

function StudentWebinars({ webinars }) {
  const order = { live: 0, scheduled: 1, archived: 2 };
  const sorted = [...webinars].sort((a, b) => order[webinarState(a)] - order[webinarState(b)]);
  if (sorted.length === 0) {
    return <div className="nb-card p-8 text-center text-slate-500" data-testid="spl-webinars-empty">No webinars or live classes scheduled for your class yet.</div>;
  }
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="spl-webinars-list">
      {sorted.map((w) => <WebinarCard key={w.update_id} w={w} />)}
    </div>
  );
}

function WebinarCard({ w }) {
  const state = webinarState(w);
  const map = {
    live: { bg: "bg-[#86EFAC]", label: "● LIVE NOW", cls: "text-red-700" },
    scheduled: { bg: "bg-[#93C5FD]", label: "SCHEDULED", cls: "" },
    archived: { bg: "bg-[#E2E8F0]", label: "ENDED", cls: "text-slate-500" },
  }[state];
  return (
    <div className={`nb-card p-4 ${map.bg}`} data-testid={`spl-webinar-${w.update_id}`}>
      <div className="flex items-center justify-between">
        <span className={`nb-badge bg-white text-xs font-bold ${map.cls}`}>{map.label}</span>
        <span className="text-xs mono">{(w.post_type || "webinar").replace("_", " ")}</span>
      </div>
      <h3 className="display text-lg font-bold mt-2">{w.title}</h3>
      {w.body && <p className="text-sm mt-1">{w.body}</p>}
      <div className="text-xs mono mt-2 space-y-0.5">
        {w.start_at && <div className="flex items-center gap-1"><Clock size={12} />{new Date(w.start_at).toLocaleString()}</div>}
        {w.host_name && <div>Host: {w.host_name}</div>}
      </div>
      {state === "archived" ? (
        w.link ? <a href={w.link} target="_blank" rel="noreferrer" className="nb-btn nb-btn-secondary mt-3 inline-flex text-xs" data-testid={`spl-webinar-replay-${w.update_id}`}><ExternalLink size={12} />Watch Recording</a>
               : <div className="text-xs text-slate-500 mt-3">Recording not available</div>
      ) : (
        (w.meeting_link || w.link) && (
          <a href={w.meeting_link || w.link} target="_blank" rel="noreferrer"
             className={`nb-btn mt-3 inline-flex text-xs ${state === "live" ? "" : "nb-btn-secondary"}`}
             data-testid={`spl-webinar-join-${w.update_id}`}>
            <Video size={12} />{state === "live" ? "Join Live" : "Open Meeting Link"}
          </a>
        )
      )}
    </div>
  );
}

function LiveWebinarDock({ webinars, onJoinTab }) {
  const live = webinars.filter((w) => webinarState(w) === "live");
  if (live.length === 0) return null;
  const w = live[0];
  return (
    <div className="fixed bottom-20 right-4 z-[60] nb-card p-3 max-w-xs shadow-lg" style={{ background: "#86EFAC" }} data-testid="spl-live-dock">
      <div className="flex items-center gap-2 text-red-700 font-bold text-xs"><Radio size={14} className="animate-pulse" />LIVE NOW</div>
      <div className="font-bold text-sm mt-1 truncate">{w.title}</div>
      <div className="flex gap-2 mt-2">
        {(w.meeting_link || w.link) && (
          <a href={w.meeting_link || w.link} target="_blank" rel="noreferrer" className="nb-btn text-xs flex-1 justify-center" data-testid="spl-live-dock-join"><Video size={12} />Join</a>
        )}
        <button onClick={onJoinTab} className="nb-btn nb-btn-ghost text-xs" data-testid="spl-live-dock-all">All ({live.length})</button>
      </div>
    </div>
  );
}

function Pill({ active, onClick, children, testid }) {
  return <button onClick={onClick} data-testid={testid}
    className={`nb-btn text-xs whitespace-nowrap shrink-0 ${active ? "" : "nb-btn-ghost"}`}>{children}</button>;
}

/* ============================ READ TO LEAD ============================ */
function StudentRead({ passages, settings, onSubmitted }) {
  const [active, setActive] = useState(null);
  if (active) return <ReadingRunner passage={active} settings={settings} onBack={() => setActive(null)} onSubmitted={() => { onSubmitted(); }} />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {passages.map((p) => (
        <button key={p.passage_id} onClick={() => setActive(p)}
          className="nb-card p-4 text-left hover:bg-[#FDE047]" data-testid={`spl-read-card-${p.passage_id}`}>
          <div className="flex items-center justify-between">
            <span className="nb-badge bg-[#93C5FD] text-xs">{p.passage_type === "WORD_LIST" ? "Word Practice" : "Fluency Passage"}</span>
            {p.my_submission?.status === "APPROVED" && <span className="nb-badge bg-[#86EFAC] text-xs">✓ Approved</span>}
          </div>
          <div className="display text-lg font-bold mt-1">{p.title}</div>
          <div className="text-xs mono mt-1 text-slate-600">{p.language_label || p.language_code} · {p.subject_name} · target {p.target_wpm} WPM</div>
          {p.my_submission && <div className="text-xs mt-2">Last: {p.my_submission.recorded_wpm} WPM · {p.my_submission.accuracy_percent}%</div>}
        </button>
      ))}
      {passages.length === 0 && <div className="nb-card p-6 text-slate-500 col-span-full text-sm">No reading assignments yet.</div>}
    </div>
  );
}

function ReadingRunner({ passage, settings, onBack, onSubmitted }) {
  const isWordList = passage.passage_type === "WORD_LIST";
  const support = speechSupport();
  const gender = settings.default_voice_gender || "female";
  if (isWordList) return <WordListMode passage={passage} support={support} gender={gender} onBack={onBack} onSubmitted={onSubmitted} />;
  return <PassageMode passage={passage} support={support} gender={gender} onBack={onBack} onSubmitted={onSubmitted} />;
}

function WordListMode({ passage, support, gender, onBack, onSubmitted }) {
  const words = passage.word_items || [];
  const [status, setStatus] = useState({}); // idx -> 'correct'
  const [xp, setXp] = useState(0);
  const listen = (w) => speak(w, { lang: passage.language_code, gender, rate: 0.75 });
  const practice = (w, idx) => {
    if (!support.stt) {
      setStatus((s) => ({ ...s, [idx]: "correct" })); setXp((x) => x + 5);
      toast.success("+5 XP (self-marked — speech not supported on this browser)");
      return;
    }
    const rec = createRecognizer(passage.language_code);
    if (!rec) return;
    let heard = "";
    rec.onresult = (e) => { for (let i = e.resultIndex; i < e.results.length; i++) heard += e.results[i][0].transcript + " "; };
    rec.onend = () => {
      const { correct } = matchWords([w], heard);
      if (correct) { setStatus((s) => ({ ...s, [idx]: "correct" })); setXp((x) => x + 5); toast.success("Correct! +5 XP"); }
      else toast.error(`Heard: "${heard.trim() || "…"}" — try again`);
    };
    rec.onerror = () => toast.error("Mic error — check permissions");
    try { rec.start(); toast.message("Listening… say the word"); } catch { /* already started */ }
  };
  const done = Object.keys(status).length;
  const submit = async () => {
    const acc = words.length ? (done / words.length) * 100 : 0;
    try {
      await api.post("/ponderr-learn/reading/submit", {
        passage_id: passage.passage_id, language_code: passage.language_code,
        recorded_wpm: 0, accuracy_percent: acc, duration_sec: 0,
        words_total: words.length, words_correct: done,
      });
      toast.success(`Submitted! ${done}/${words.length} words · +${xp} XP`); onSubmitted(); onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="spl-wordlist-mode">
      <Header passage={passage} onBack={onBack} xp={xp} />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {words.map((w, idx) => (
          <div key={idx} className={`nb-card p-5 text-center space-y-3 ${status[idx] === "correct" ? "bg-[#86EFAC]" : "bg-white"}`} data-testid={`spl-word-card-${idx}`}>
            <div className="display text-3xl font-black">{w}</div>
            <div className="flex justify-center gap-2">
              <button onClick={() => listen(w)} className="nb-btn nb-btn-secondary text-xs" data-testid={`spl-word-listen-${idx}`}><Volume2 size={14} />Listen</button>
              <button onClick={() => practice(w, idx)} className="nb-btn text-xs" data-testid={`spl-word-speak-${idx}`}><Mic size={14} />Speak</button>
            </div>
            {status[idx] === "correct" && <div className="text-xs font-bold text-emerald-800">✓ +5 XP</div>}
          </div>
        ))}
      </div>
      <button onClick={submit} className="nb-btn w-full justify-center" data-testid="spl-wordlist-submit"><Check size={16} />Finish & Submit ({done}/{words.length})</button>
    </div>
  );
}

function PassageMode({ passage, support, gender, onBack, onSubmitted }) {
  const words = useMemo(() => tokenize(passage.content_text), [passage]);
  const offsets = useMemo(() => {
    const arr = []; let pos = 0;
    words.forEach((w) => { const idx = passage.content_text.indexOf(w, pos); arr.push(idx); pos = idx + w.length; });
    return arr;
  }, [words, passage]);
  const [hi, setHi] = useState(-1);      // karaoke highlight index
  const [statuses, setStatuses] = useState([]); // read-aloud match statuses
  const [listening, setListening] = useState(false);
  const [result, setResult] = useState(null);
  const sessionRef = useRef(null);
  const recorderRef = useRef(null);
  const audioBlobRef = useRef(null);
  const startTime = useRef(0);
  const heard = useRef("");

  const listenMode = () => {
    cancelSpeak(); setHi(0);
    speak(passage.content_text, {
      lang: passage.language_code, gender, rate: 0.85,
      onBoundary: (charIndex) => {
        let idx = 0;
        for (let i = 0; i < offsets.length; i++) { if (offsets[i] <= charIndex) idx = i; else break; }
        setHi(idx);
      },
      onEnd: () => setHi(-1),
    });
  };

  const startRead = async () => {
    if (!support.stt) { toast.error("Speech recognition not supported here — use ‘I read it’ below."); return; }
    const session = createReadingSession(passage.language_code, {
      onTranscript: (full) => setStatuses(matchWords(words, full).statuses),
    });
    if (!session) { toast.error("Speech recognition unavailable"); return; }
    heard.current = ""; setStatuses([]); setResult(null); audioBlobRef.current = null;
    // Capture microphone audio alongside recognition (best-effort, zero-cost).
    try {
      const rec = await createAudioRecorder();
      if (rec) { recorderRef.current = rec; rec.start(); }
    } catch { recorderRef.current = null; }
    sessionRef.current = session;
    startTime.current = Date.now();
    session.start();
    setListening(true);
    toast.message("Reading… read the whole passage, then tap Stop");
  };

  const stopRead = async () => {
    const session = sessionRef.current;
    const full = session ? session.stop() : heard.current;
    sessionRef.current = null;
    heard.current = full;
    setListening(false);
    if (recorderRef.current) {
      try { audioBlobRef.current = await recorderRef.current.stop(); } catch { /* */ }
      recorderRef.current = null;
    }
    finish(full);
  };

  // Grade ONLY on stop, against the full accumulated transcript + full passage.
  const finish = (full) => {
    const elapsed = Math.max(1, (Date.now() - startTime.current) / 1000);
    const m = matchWords(words, full);
    const spokenCount = tokenize(full).length;
    const wpm = Math.round((spokenCount / elapsed) * 60);
    const acc = m.total ? Math.round((m.correct / m.total) * 100) : 0;
    setStatuses(m.statuses);
    setResult({ wpm, acc, correct: m.correct, total: m.total, duration: Math.round(elapsed) });
  };

  const uploadAudio = async () => {
    if (!audioBlobRef.current) return null;
    try {
      const fd = new FormData();
      fd.append("file", new File([audioBlobRef.current], "recording.webm",
        { type: audioBlobRef.current.type || "audio/webm" }));
      const up = await api.post("/ponderr-learn/files/upload", fd);
      return up.data.storage_path;
    } catch { return null; }
  };

  const submit = async (manual) => {
    const audio_blob_url = manual ? null : await uploadAudio();
    const payload = manual
      ? { recorded_wpm: 0, accuracy_percent: 100, duration_sec: 0, words_total: words.length, words_correct: words.length }
      : { recorded_wpm: result.wpm, accuracy_percent: result.acc, duration_sec: result.duration, words_total: result.total, words_correct: result.correct };
    try {
      const r = await api.post("/ponderr-learn/reading/submit", { passage_id: passage.passage_id, language_code: passage.language_code, audio_blob_url, ...payload });
      toast.success(`Submitted! AI mark ${r.data.ai_suggested_mark}/${passage.max_marks} · +${r.data.xp} XP`);
      onSubmitted(); onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="spl-passage-mode">
      <Header passage={passage} onBack={onBack} />
      <div className="flex flex-wrap gap-2">
        <button onClick={listenMode} className="nb-btn nb-btn-secondary" data-testid="spl-listen-mode"><Volume2 size={16} />Listen Mode</button>
        {!listening
          ? <button onClick={startRead} className="nb-btn" data-testid="spl-read-start"><Mic size={16} />Read Aloud Practice</button>
          : <button onClick={stopRead} className="nb-btn nb-btn-danger" data-testid="spl-read-stop"><Square size={16} />Stop</button>}
      </div>
      <div className="nb-card p-5 leading-relaxed text-lg" data-testid="spl-passage-text">
        {words.map((w, i) => {
          let cls = "";
          if (i === hi) cls = "bg-[#FDE047] rounded px-0.5";
          else if (statuses[i] === "correct") cls = "text-emerald-700 font-semibold";
          else if (statuses[i] === "wrong" && (result || listening)) cls = "text-amber-600";
          return <span key={i} className={cls}>{w} </span>;
        })}
      </div>
      {result && (
        <div className="nb-card p-4 bg-[#86EFAC] flex flex-wrap gap-4 items-center" data-testid="spl-read-result">
          <div><div className="nb-label">WPM</div><div className="display text-3xl font-black">{result.wpm}</div></div>
          <div><div className="nb-label">Accuracy</div><div className="display text-3xl font-black">{result.acc}%</div></div>
          <div className="text-sm">{result.correct}/{result.total} words · {result.duration}s</div>
          <button onClick={() => submit(false)} className="nb-btn ml-auto" data-testid="spl-read-submit"><Check size={16} />Submit Attempt</button>
        </div>
      )}
      {!support.stt && (
        <div className="nb-card p-4 bg-[#FDE047] text-sm space-y-2">
          <div>Speech recognition isn’t available in this browser (try Chrome/Edge). You can still self-mark:</div>
          <button onClick={() => submit(true)} className="nb-btn" data-testid="spl-read-selfmark"><Check size={16} />I read it aloud</button>
        </div>
      )}
    </div>
  );
}

function Header({ passage, onBack, xp }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <button onClick={onBack} className="nb-btn nb-btn-ghost text-sm">← Back</button>
      <div className="text-right min-w-0">
        <div className="display text-lg font-bold truncate">{passage.title}</div>
        <div className="text-xs mono text-slate-500">{passage.language_label || passage.language_code}</div>
      </div>
      {xp != null && <span className="nb-badge bg-[#FDE047]"><Award size={12} />{xp} XP</span>}
    </div>
  );
}

/* ============================ ART STUDIO ============================ */
function StudentArt({ prompts, onSubmitted }) {
  const [active, setActive] = useState(null);
  if (active) return <ArtStudio prompt={active} onBack={() => setActive(null)} onSubmitted={onSubmitted} />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {prompts.map((p) => (
        <button key={p.prompt_id} onClick={() => setActive(p)} className="nb-card p-4 text-left hover:bg-[#FDE047]" data-testid={`spl-art-card-${p.prompt_id}`}>
          <div className="flex items-center justify-between">
            <span className="nb-badge bg-[#86EFAC] text-xs">{p.submission_type}</span>
            {p.my_submission?.status === "APPROVED" && <span className="nb-badge bg-[#93C5FD] text-xs">✓ Scored {p.my_submission.teacher_score}</span>}
          </div>
          <div className="display text-lg font-bold mt-1">{p.title}</div>
          <div className="text-xs mt-1">{p.description}</div>
        </button>
      ))}
      {prompts.length === 0 && <div className="nb-card p-6 text-slate-500 col-span-full text-sm">No art prompts yet.</div>}
    </div>
  );
}

function ArtStudio({ prompt, onBack, onSubmitted }) {
  const canRef = useRef(null);
  const [mode, setMode] = useState(prompt.submission_type === "PHOTO" ? "PHOTO" : "CANVAS");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      let file;
      if (mode === "CANVAS") {
        const blob = await canRef.current.exportBlob();
        if (!blob || blob.size === 0) { toast.error("Draw something first"); setBusy(false); return; }
        file = new File([blob], "drawing.png", { type: "image/png" });
      } else {
        if (!photo) { toast.error("Choose a photo first"); setBusy(false); return; }
        file = photo;
      }
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/ponderr-learn/files/upload", fd);
      await api.post("/ponderr-learn/art/submit", { prompt_id: prompt.prompt_id, storage_path: up.data.storage_path, mode });
      toast.success("Artwork submitted! +10 XP"); onSubmitted(); onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="spl-art-studio">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="nb-btn nb-btn-ghost text-sm">← Back</button>
        <div className="text-right"><div className="display text-lg font-bold">{prompt.title}</div></div>
      </div>
      <div className="nb-card p-4 bg-[#FDE047] text-sm">{prompt.description}</div>
      {prompt.submission_type === "BOTH" && (
        <div className="flex gap-2">
          <button onClick={() => setMode("CANVAS")} className={`nb-btn text-xs ${mode === "CANVAS" ? "" : "nb-btn-ghost"}`} data-testid="spl-art-mode-canvas">On-Screen Canvas</button>
          <button onClick={() => setMode("PHOTO")} className={`nb-btn text-xs ${mode === "PHOTO" ? "" : "nb-btn-ghost"}`} data-testid="spl-art-mode-photo">Upload Photo</button>
        </div>
      )}
      {mode === "CANVAS"
        ? <div className="nb-card p-4"><DrawingCanvas canvasRef={canRef} /></div>
        : (
          <div className="nb-card p-6 space-y-3">
            <label className="nb-btn nb-btn-secondary cursor-pointer inline-flex">
              <Camera size={16} />{photo ? "Change photo" : "Snap / Upload photo"}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setPhoto(e.target.files[0])} data-testid="spl-art-photo" />
            </label>
            {photo && <div className="text-sm text-emerald-700">✓ {photo.name}</div>}
          </div>
        )}
      <button onClick={submit} disabled={busy} className="nb-btn w-full justify-center" data-testid="spl-art-submit"><Upload size={16} />{busy ? "Submitting…" : "Submit Artwork"}</button>
    </div>
  );
}

/* ============================ NOTES & LESSONS ============================ */
function StudentNotes({ notes, settings }) {
  const [active, setActive] = useState(null);
  useEffect(() => { if (notes.length && !active) setActive(notes[0]); }, [notes]);
  if (!notes.length) return <div className="nb-card p-6 text-slate-500 text-sm">No lesson notes published yet.</div>;
  return (
    <div className="space-y-3">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {notes.map((n) => (
          <button key={n.note_id} onClick={() => setActive(n)} className={`nb-btn text-xs shrink-0 ${active?.note_id === n.note_id ? "" : "nb-btn-ghost"}`} data-testid={`spl-note-tab-${n.note_id}`}>{n.title}</button>
        ))}
      </div>
      {active && <NoteViewer note={active} settings={settings} />}
    </div>
  );
}

function NoteViewer({ note, settings }) {
  const langs = settings.enabled_languages || [];
  const translations = note.translations || [];
  const [trLang, setTrLang] = useState(translations[0]?.language_code || "");
  const [popover, setPopover] = useState(null); // {word, def, phonetic, loading}
  const gender = settings.default_voice_gender || "female";

  const activeTr = translations.find((t) => t.language_code === trLang);

  const inspect = async (e) => {
    let word = "";
    if (document.caretRangeFromPoint) {
      const range = document.caretRangeFromPoint(e.clientX, e.clientY);
      if (range) {
        const node = range.startContainer;
        const text = node.textContent || "";
        let s = range.startOffset, en = range.startOffset;
        while (s > 0 && /\w/.test(text[s - 1])) s--;
        while (en < text.length && /\w/.test(text[en])) en++;
        word = text.slice(s, en);
      }
    }
    if (!word) word = (window.getSelection?.().toString() || "").trim().split(/\s+/)[0];
    word = (word || "").toLowerCase().replace(/[^a-z']/g, "");
    if (!word) return;
    setPopover({ word, loading: true });
    speak(word, { lang: note.translations?.[0]?.language_code || "en-IN", gender, rate: 0.8 });
    try {
      const r = await api.get("/ponderr-learn/dictionary", { params: { q: word } });
      const hit = (r.data || []).find((d) => d.keyword === word) || r.data?.[0];
      if (hit) { setPopover({ word, def: hit.definition, phonetic: hit.phonetic, icon: hit.svg_icon_url, loading: false }); return; }
    } catch { /* */ }
    // Fallback: free dictionaryapi.dev (zero-cost, no key)
    try {
      const res = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word)}`);
      const j = await res.json();
      const meaning = j?.[0]?.meanings?.[0]?.definitions?.[0]?.definition;
      const ph = j?.[0]?.phonetic;
      setPopover({ word, def: meaning || "No definition found.", phonetic: ph || "", loading: false });
    } catch { setPopover({ word, def: "No definition found.", loading: false }); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="nb-card p-5 space-y-3" data-testid="spl-note-primary">
        <div className="flex items-center justify-between">
          <h3 className="display text-xl font-bold">{note.title}</h3>
          <button onClick={() => speak(note.body_html.replace(/<[^>]+>/g, " "), { lang: "en-IN", gender, rate: 0.9 })} className="nb-btn nb-btn-secondary text-xs" data-testid="spl-note-play"><Volume2 size={14} />Read aloud</button>
        </div>
        {note.audio_url && <audio src={note.audio_url} controls className="w-full" data-testid="spl-note-audio" />}
        <div className="text-xs text-slate-500">Tip: tap any word to hear it & see its meaning.</div>
        <div className="prose max-w-none text-sm cursor-help" onClick={inspect}
          data-testid="spl-note-body" dangerouslySetInnerHTML={{ __html: note.body_html || "<i>No content.</i>" }} />
        {popover && (
          <div className="nb-card p-3 bg-[#FDE047] space-y-1" data-testid="spl-note-popover">
            <div className="flex items-center gap-2">
              {popover.icon && <img src={popover.icon} alt="" className="w-6 h-6" />}
              <b className="capitalize">{popover.word}</b>
              {popover.phonetic && <span className="text-xs mono text-slate-600">{popover.phonetic}</span>}
              <button onClick={() => setPopover(null)} className="ml-auto nb-btn nb-btn-ghost text-xs">×</button>
            </div>
            <div className="text-sm">{popover.loading ? "Looking up…" : popover.def}</div>
          </div>
        )}
      </div>
      <div className="nb-card p-5 space-y-3" data-testid="spl-note-translation">
        <div className="flex items-center justify-between">
          <div className="display text-lg font-bold">Translation Companion</div>
          <select className="nb-select text-xs" value={trLang} onChange={(e) => setTrLang(e.target.value)} data-testid="spl-note-tr-lang">
            <option value="">Select Language</option>
            {translations.map((t) => <option key={t.language_code} value={t.language_code}>{t.language_label || t.language_code}</option>)}
          </select>
        </div>
        {activeTr ? (
          <div className="space-y-2">
            <button onClick={() => speak((activeTr.body_html || "").replace(/<[^>]+>/g, " "), { lang: activeTr.language_code, gender, rate: 0.85 })} className="nb-btn nb-btn-secondary text-xs" data-testid="spl-note-tr-play"><Volume2 size={14} />Listen</button>
            <div className="prose max-w-none text-sm" dangerouslySetInnerHTML={{ __html: activeTr.body_html }} />
          </div>
        ) : (
          <div className="text-sm text-slate-500">{translations.length ? "Select a language to see the mirrored translation." : "No translation mirrors for this note."}</div>
        )}
      </div>
    </div>
  );
}

/* ============================ LIFE SKILLS HUB ============================ */
function ytId(url) {
  const m = /(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{6,})/.exec(url || "");
  return m ? m[1] : null;
}

function LessonView({ lesson }) {
  const kind = lesson.kind;
  const html = lesson.html_content || lesson.notes_html || "";
  const yid = ytId(lesson.youtube_url);
  return (
    <div className="nb-card p-4 bg-white space-y-2" data-testid={`spl-ls-lesson-${lesson.lesson_id}`}>
      <div className="flex items-center gap-2">
        <PlayCircle size={16} className="text-[#7C3AED]" />
        <b className="text-sm">{lesson.title}</b>
        {lesson.duration_min ? <span className="text-xs mono text-slate-500">· {lesson.duration_min} min</span> : null}
      </div>
      {lesson.description && <p className="text-xs text-slate-600">{lesson.description}</p>}
      {kind === "video_youtube" && yid && (
        <div className="aspect-video w-full overflow-hidden rounded-lg border-2 border-black">
          <iframe title={lesson.title} src={`https://www.youtube.com/embed/${yid}`}
            className="w-full h-full" allowFullScreen data-testid={`spl-ls-yt-${lesson.lesson_id}`} />
        </div>
      )}
      {kind === "video_upload" && lesson.storage_path && (
        <video src={lesson.storage_path} controls className="w-full rounded-lg border-2 border-black" />
      )}
      {kind === "audio" && lesson.storage_path && (
        <audio src={lesson.storage_path} controls className="w-full" />
      )}
      {kind === "pdf" && lesson.storage_path && (
        <a href={lesson.storage_path} target="_blank" rel="noreferrer"
          className="nb-btn nb-btn-secondary inline-flex text-xs" data-testid={`spl-ls-pdf-${lesson.lesson_id}`}>
          <ExternalLink size={12} />Open PDF
        </a>
      )}
      {(kind === "notes" || kind === "html") && html && (
        <div className="prose max-w-none text-sm" dangerouslySetInnerHTML={{ __html: html }} />
      )}
    </div>
  );
}

function CourseCard({ course }) {
  const [openCh, setOpenCh] = useState(course.chapters?.[0]?.chapter_id || null);
  return (
    <div className="nb-card p-4 !bg-[#FBCFE8]" data-testid={`spl-ls-course-${course.curriculum_id}`}>
      <div className="flex items-center gap-2">
        <Sparkles size={18} className="text-[#7C3AED]" />
        <h3 className="display text-lg font-bold">{course.subject_name} Course</h3>
      </div>
      <div className="mt-3 space-y-2">
        {course.chapters.map((ch) => (
          <div key={ch.chapter_id} className="nb-card bg-white overflow-hidden">
            <button onClick={() => setOpenCh(openCh === ch.chapter_id ? null : ch.chapter_id)}
              className="w-full flex items-center justify-between p-3 text-left"
              data-testid={`spl-ls-chapter-${ch.chapter_id}`}>
              <span className="font-bold text-sm">{ch.title}</span>
              <span className="flex items-center gap-2 text-xs mono text-slate-500">
                {ch.lessons.length} lesson{ch.lessons.length === 1 ? "" : "s"}
                <ChevronRight size={16} className={`transition-transform ${openCh === ch.chapter_id ? "rotate-90" : ""}`} />
              </span>
            </button>
            {openCh === ch.chapter_id && (
              <div className="p-3 pt-0 space-y-2">
                {ch.description && <p className="text-xs text-slate-600">{ch.description}</p>}
                {ch.lessons.map((l) => <LessonView key={l.lesson_id} lesson={l} />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StudentLifeSkills({ courses }) {
  if (!courses.length) {
    return <div className="nb-card p-8 text-center text-slate-500" data-testid="spl-life-skills-empty">
      No Life Skills courses published for your class yet. Check back soon!
    </div>;
  }
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="spl-life-skills-list">
      {courses.map((c) => <CourseCard key={c.curriculum_id} course={c} />)}
    </div>
  );
}

/* ============================ CODING HUB ============================ */
const CODING_META = {
  code_org: { label: "Code.org", bg: "!bg-[#FDE047]" },
  makecode: { label: "MakeCode", bg: "!bg-[#93C5FD]" },
  scratch: { label: "Scratch", bg: "!bg-[#FCA5A5]" },
  tynker: { label: "Tynker", bg: "!bg-[#C4B5FD]" },
  other: { label: "Coding", bg: "!bg-[#86EFAC]" },
};

function StudentCoding({ activities }) {
  if (!activities.length) {
    return <div className="nb-card p-8 text-center text-slate-500" data-testid="spl-coding-empty">
      No coding activities assigned to your class yet.
    </div>;
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="spl-coding-list">
      {activities.map((a) => {
        const meta = CODING_META[a.provider] || CODING_META.other;
        return (
          <div key={a.activity_id} className={`nb-card p-4 ${meta.bg} flex flex-col`} data-testid={`spl-coding-${a.activity_id}`}>
            <div className="flex items-center justify-between">
              <span className="nb-badge bg-white text-xs font-bold">{meta.label}</span>
              <Code2 size={16} />
            </div>
            {a.thumbnail_url && (
              <img src={a.thumbnail_url} alt="" className="w-full h-28 object-cover rounded-lg border-2 border-black mt-2" />
            )}
            <h3 className="display text-base font-bold mt-2">{a.title}</h3>
            {a.description && <p className="text-xs mt-1 flex-1">{a.description}</p>}
            <a href={a.url} target="_blank" rel="noreferrer"
              className="nb-btn mt-3 inline-flex justify-center text-xs" data-testid={`spl-coding-launch-${a.activity_id}`}>
              <PlayCircle size={14} />Launch Activity
            </a>
          </div>
        );
      })}
    </div>
  );
}
